import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface InputTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

export default function InputTypeSelector({ value, onChange, label }: InputTypeSelectorProps) {
  // Determine if current value is custom (pipe-delimited)
  const isCustomValue = value.includes('|');
  const [inputTypeMode, setInputTypeMode] = useState(
    isCustomValue ? 'custom' : value || 'checkBox'
  );
  const [customOptions, setCustomOptions] = useState(
    isCustomValue ? value : ''
  );

  // Sync internal state when value prop changes (handles reordering/deletion of dynamic tasks)
  useEffect(() => {
    const newIsCustom = value.includes('|');
    const isStandardType = ['checkBox', 'number', 'text'].includes(value);
    
    if (newIsCustom) {
      setInputTypeMode('custom');
      setCustomOptions(value);
    } else if (isStandardType) {
      setInputTypeMode(value);
      setCustomOptions('');
    } else if (value && !isStandardType) {
      // If value is not a standard type and not empty, it's custom (even without pipes yet)
      setInputTypeMode('custom');
      setCustomOptions(value);
    } else {
      // Empty or undefined - default to checkBox
      setInputTypeMode('checkBox');
      setCustomOptions('');
    }
  }, [value]);

  useEffect(() => {
    if (inputTypeMode !== 'custom') {
      onChange(inputTypeMode);
    }
  }, [inputTypeMode, onChange]);

  const handleCustomChange = (newValue: string) => {
    setCustomOptions(newValue);
    onChange(newValue);
  };

  return (
    <div className="space-y-3">
      {label && <Label>{label}</Label>}
      <Select value={inputTypeMode} onValueChange={setInputTypeMode}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="checkBox">Checkbox</SelectItem>
          <SelectItem value="number">Number</SelectItem>
          <SelectItem value="text">Text</SelectItem>
          <SelectItem value="custom">Custom Options (pipe-delimited)</SelectItem>
        </SelectContent>
      </Select>

      {inputTypeMode === 'custom' && (
        <div>
          <Label>Custom Options (separate with |)</Label>
          <Input
            value={customOptions}
            onChange={(e) => handleCustomChange(e.target.value)}
            placeholder="option1|option2|option3"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Example: happy|ok|sad
          </p>
        </div>
      )}
    </div>
  );
}
